import styled from 'vue-styled-components';

import { fromEvent } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import CommonButton from '@design/components/button/Button';

import FinderIcon from '@svgIcons/system-finder-stroke.svg';
import CircleCloseFillIcon from '@svgIcons/circle-close-fill.svg';

import {
    innerHtmlJSX,
    isEllipsisActive,
    isValueExist,
    looseSearch,
} from '@/utils/utils';

import { GRAY } from '@styles/common.styled';
import { FLEX_COLUMN_DIV } from '@/styles/common.styled.component';
import { debounce } from 'lodash';

export default {
    name: 'DropdownMixins',
    props: {
        title: {
            type: HTMLElement | String,
            default: null,
        },
        isVisible: {
            type: Boolean,
            default: false,
        },
        hasSearchBar: {
            type: Boolean,
            default: false,
        },
        optionList: {
            type: Array,
            default() {
                return [];
            },
        },
        hasConfirmButton: {
            type: Boolean,
            default: false,
        },
        hasCancelButton: {
            type: Boolean,
            default: false,
        },
        translator: {
            type: Function,
            default: (v) => v,
        },
        width: {
            type: [String, Number],
            default: 320,
        },
        height: {
            type: Number,
            default: 350,
        },
        customItemRenderer: {
            type: [Function, Object],
            default: null,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        showSelected: {
            type: Boolean,
            default: true,
        },
        allowEmptyValue: {
            type: Boolean,
            default: false,
        },
        tooltipGenerator: {
            type: Function,
            default: R.always(''),
        },
        customCss: undefined,
        offset: {
            type: Object,
            default() {
                return {};
            },
        },
        tooltipOnEllipsis: {
            type: Boolean,
            default: false,
        },
        useLooseSearch: {
            type: Boolean,
            default: false,
        },
        addtionalToBodyPosition: {
            type: Object,
            default() {
                return {};
            },
        },
        shouldAdjustWindowOverflow: {
            type: Boolean,
            default: false,
        },
        hasAddBtn: Boolean,
        addBtnLocaleKey: String,
        useVirtualScroll: Boolean,
    },
    data() {
        return {
            keyword: '',
            parentElement: undefined,
            dropdownElement: undefined,
            debouncedKeyword: '',
            isComposing: false,
        };
    },
    computed: {
        filteredOptions() {
            if (!this.isVisible) {
                return [];
            }

            const {
                optionList,
                debouncedKeyword,
                translator = R.identity,
                normalizeText,
                useLooseSearch,
            } = this;

            if (!debouncedKeyword) {
                return optionList;
            }

            if (useLooseSearch) {
                return looseSearch(
                    optionList,
                    R.compose(String, translator),
                    debouncedKeyword
                );
            } else {
                const normalizedKeyword = normalizeText(debouncedKeyword);

                return optionList.filter((option = '') => {
                    const type = R.propOr('option', 'type', option);
                    if (type === 'category') return true;

                    const text = String(translator(option));

                    return (
                        text
                            .toLowerCase()
                            .replace(/[^a-zA-Z0-9가-힣]+/gi, '')
                            .indexOf(normalizedKeyword) > -1
                    );
                });
            }
        },
    },
    methods: {
        renderOption(option) {
            const { customItemRenderer, translator } = this;

            if (customItemRenderer) {
                return customItemRenderer(option) || translator(option);
            } else {
                return translator(option);
            }
        },
        submit({ value, isCheckBox = false }) {
            const { allowEmptyValue, currentSelectedOption } = this;

            let confirmedValue = isCheckBox ? currentSelectedOption : value;

            if (!allowEmptyValue) {
                const isEmpty = isCheckBox
                    ? !currentSelectedOption.length > 0
                    : !value;

                if (isEmpty) return;
            }

            this.$emit('confirm', confirmedValue);
        },
        normalizeText(text) {
            if (!text) return '';

            return String(text).replace(/\s+/g, '').toLowerCase();
        },

        renderTitle() {
            const { title } = this;

            return typeof title === 'string' ? <Title>{title}</Title> : title;
        },
        renderEmptyOption() {
            const { keyword, hasAddBtn, addBtnLocaleKey } = this;

            return (
                <EmptyWrapper>
                    {hasAddBtn && (
                        <AddButton
                            {...innerHtmlJSX(
                                this.$t(addBtnLocaleKey, { keyword })
                            )}
                            onClick={() => {
                                this.$emit('add:option', keyword);
                            }}
                        />
                    )}
                    <EmptyText>
                        {this.$t('NEW_DASHBOARD.filter_search_no_data')}
                    </EmptyText>
                </EmptyWrapper>
            );
        },
        renderSearchInput() {
            return this.hasSearchBar ? (
                <SearchHeader>
                    <input
                        value={this.keyword}
                        onInput={(e) => {
                            this.keyword = e.target.value;
                            this.debouncedSearch(e.target.value);
                        }}
                        onKeydown={(e) => {
                            if (
                                this.hasAddBtn &&
                                e.key === 'Enter' &&
                                !e.isComposing &&
                                !isValueExist(this.filteredOptions)
                            ) {
                                this.$emit('add:option', this.keyword);
                            }
                        }}
                        placeholder={this.$t('NEW_DASHBOARD.filter_search')}
                        class="search-bar"
                        ref="keywordInputbox"
                    />
                    <div class="icon-wrapper">
                        <FinderIcon viewBox="0 0 20 20" width="16px" height="16px" />
                    </div>
                    {this.keyword && (
                        <button
                            type="button"
                            class="clear-button"
                            aria-label={this.$t('COMMON.clear')}
                            onMousedown={(e) => e.preventDefault()}
                            onClick={(e) => {
                                // The button unmounts as soon as the keyword
                                // empties, so by the time this click reaches the
                                // window-level outside-click check its target is
                                // detached and the dropdown would close. Keep the
                                // click inside.
                                e.stopPropagation();
                                this.keyword = '';
                                this.debouncedSearch('');
                                const input = this.$refs.keywordInputbox;
                                input && input.focus();
                            }}
                        >
                            <CircleCloseFillIcon viewBox="0 0 16 16" />
                        </button>
                    )}
                </SearchHeader>
            ) : null;
        },
        renderCommonButtons() {
            const { hasCancelButton, hasConfirmButton, initOptions, submit } =
                this;

            const renderBtn = hasCancelButton || hasConfirmButton;

            return renderBtn ? (
                <ButtonWrapper>
                    {hasCancelButton && (
                        <CommonButton
                            type="white"
                            onClick={() => initOptions()}
                        >
                            {this.$t('COMMON.cancel')}
                        </CommonButton>
                    )}

                    {hasConfirmButton && (
                        <CommonButton type="primary" onClick={() => submit()}>
                            {this.$t('COMMON.confirm')}
                        </CommonButton>
                    )}
                </ButtonWrapper>
            ) : null;
        },
        initOptions() {
            this.keyword = '';
            this.currentSelectedOption = this.selectedOption;
            this.$emit('cancel');
        },
        handleClickOutside(e) {
            // e.preventDefault();
            e.stopPropagation();

            const { $refs, isVisible } = this;

            const wrapper = $refs.dropboxWrapper;

            if (wrapper && isVisible) {
                if (!wrapper.$el.contains(e.target)) {
                    this.$emit('clickOutside', e);
                }
            }
        },
        handleScroll(e) {
            e.preventDefault();
            e.stopPropagation();

            this.isVisible && this.$emit('clickOutside');
        },
        updateElementPosition(isVisible) {
            const {
                parentElement,
                addtionalToBodyPosition,
                shouldAdjustWindowOverflow,
            } = this;
            const wrapper = this.$refs.dropboxWrapper;

            if (!parentElement) {
                this.parentElement = wrapper.$el.parentElement;
                this.dropdownElement = wrapper.$el;
            }

            if (wrapper && isVisible) {
                const _parentElement =
                    this.parentElement || wrapper.$el.parentElement;

                if (!_parentElement) {
                    return;
                }

                wrapper.$el.style.left = `0px`;
                wrapper.$el.style.top = `0px`;

                const { x, y } = _parentElement.getBoundingClientRect();
                const { width: dropdownWidth, height: dropdownHeight } =
                    wrapper.$el.getBoundingClientRect();

                let { top = 0, left = 0 } = this.offset;

                if (isValueExist(addtionalToBodyPosition)) {
                    const { top: aTop = 0, left: aLeft = 0 } =
                        addtionalToBodyPosition;
                    top += aTop;
                    left += aLeft;
                }

                let dropdownLeft = x + left;
                let dropdownTop = y + top;

                if (shouldAdjustWindowOverflow) {
                    const padding = 10;
                    if (dropdownWidth > window.innerWidth) {
                        dropdownLeft = padding;
                    } else if (
                        dropdownLeft + dropdownWidth >
                        window.innerWidth
                    ) {
                        dropdownLeft =
                            window.innerWidth - dropdownWidth - padding;
                    }

                    if (dropdownHeight > window.innerHeight) {
                        dropdownTop = padding;
                    } else if (
                        dropdownTop + dropdownHeight >
                        window.innerHeight
                    ) {
                        dropdownTop =
                            window.innerHeight - dropdownHeight - padding;
                    }

                    if (dropdownLeft < 0) {
                        dropdownLeft = padding;
                    }

                    if (dropdownTop < 0) {
                        dropdownTop = padding;
                    }
                }

                wrapper.$el.style.left = `${dropdownLeft}px`;
                wrapper.$el.style.top = `${dropdownTop}px`;

                document.body.appendChild(wrapper.$el);
            }

            if (!isVisible && parentElement) {
                parentElement.appendChild(wrapper.$el);
            }
        },
        shouldDisplayTooltip(id) {
            const $el = document.getElementById(id);

            return $el ? isEllipsisActive($el) : false;
        },
    },
    beforeDestroy() {
        try {
            const wrapper = this.$refs.dropboxWrapper;

            if (wrapper) {
                document.body.removeChild(wrapper.$el);
            }
        } catch {}
    },
    mounted() {
        const {
            generateWatchNew$: gen$,
            handleClickOutside,
            updateElementPosition,
            appendToBody,
            handleScroll,
            $refs,
        } = this;

        /**
         * Left container event only works with addEventListener
         */
        const leftContainer = document.getElementById(
            'dashboard-left-container'
        );

        if (leftContainer) {
            leftContainer.addEventListener('click', () => {
                if (this.dropdownElement) {
                    this.dropdownElement.remove();
                }
            });
        }

        this.$subscribeTo(gen$('isVisible'), () => {
            setTimeout(() => {
                if ($refs.keywordInputbox) {
                    $refs.keywordInputbox.focus();
                }

                if ($refs.dropboxWrapper && this.useVirtualScroll) {
                    const dropdownVirtualScroller =
                        $refs.dropboxWrapper.$el.querySelector(
                            '.dropdown-virtual-scroller'
                        );

                    if (dropdownVirtualScroller) {
                        const scrollTop = dropdownVirtualScroller.scrollTop;
                        if (scrollTop > 0) {
                            dropdownVirtualScroller.scrollTop = scrollTop - 1;
                        }
                    }
                }
            }, 0);
        });

        this.$subscribeTo(fromEvent(window, 'click'), handleClickOutside);

        if (appendToBody) {
            const contentArea = document.getElementById(
                'dashboard-content-area'
            );

            this.$subscribeTo(
                gen$('isVisible').pipe(filter(R.identity)),
                updateElementPosition
            );

            this.$subscribeTo(fromEvent(window, 'scroll'), handleScroll);

            contentArea &&
                this.$subscribeTo(
                    fromEvent(contentArea, 'scroll'),
                    handleScroll
                );

            const width$ = gen$('windowDimension.width', {
                immediate: false,
            }).pipe(distinctUntilChanged());

            this.$subscribeTo(width$, () => this.$emit('clickOutside'));
        }

        this.debouncedSearch = debounce((value) => {
            this.debouncedKeyword = value;
        }, 300);
    },
};

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: right;
    padding: 12px 15px;

    button {
        padding: 9px 16px;
        height: 36px;
        font-size: 13px;
        font-weight: 500;
        margin-left: 10px;

        &.white {
            border: none;
        }
    }
`;

// Same field as `Common/searchInput` (TASK-14078), inlined here because the
// dropdown owns the keyword state and the add-on-Enter behaviour.
const SearchHeader = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    padding: 12px;

    .search-bar {
        width: 100%;
        min-width: 0;
        height: 36px;
        padding: 0 32px 0 30px;
        border: 1px solid var(--color-gray-40, #d9dee3);
        border-radius: 8px;
        background: var(--color-gray-white, #fff);
        color: var(--color-gray-100, #3c464f);
        font-size: 14px;
        line-height: 21px;
        letter-spacing: -0.14px;
        text-overflow: ellipsis;

        &:focus {
            border-color: var(--color-blue-50, #2f80ed);
            outline: none;
        }
        &::placeholder {
            color: var(--color-gray-70, #949aa1);
        }
    }

    .icon-wrapper {
        position: absolute;
        left: 22px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        color: var(--color-gray-70, #949aa1);
        pointer-events: none;

        /* Same as Common/searchInput: keep the 20px icon's 1px stroke. */
        path {
            vector-effect: non-scaling-stroke;
        }
    }

    &:focus-within .icon-wrapper {
        color: var(--color-blue-60, #0e74f5);
    }

    .clear-button {
        position: absolute;
        right: 22px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        padding: 0;
        border: none;
        background: transparent;
        color: var(--color-gray-70, #949aa1);
        cursor: pointer;

        svg {
            width: 16px;
            height: 16px;
        }

        &:hover {
            color: var(--color-gray-100, #3c464f);
        }
    }
`;

const Title = styled.div`
    padding: 10px 15px;
    color: ${GRAY(100)};
    font-size: 13px;
    font-weight: 500;
    border-bottom: 1px solid ${GRAY(25)};
`;

const EmptyWrapper = styled(FLEX_COLUMN_DIV)`
    height: 200px;
    color: ${GRAY(40)};
    font-size: 13px;
    font-weight: 500;
    white-space: pre-line;
    text-align: center;
`;

const EmptyText = styled.div`
    margin: auto 0;
    padding-bottom: 20px;
`;

const AddButton = styled.button`
    height: 40px;
    margin: 8px 16px;
    padding: 0;

    font-size: 13px;
    font-weight: 500;
    color: #3c464f;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    .keyword {
        font-weight: 700;
    }
`;
